// src/redux/baslikTemplateSlice.js
import { createSlice } from '@reduxjs/toolkit';

// Templates artık backend'de tutuluyor. localStorage'a büyük dxfRawText yazmıyoruz;
// JSON.stringify üzerinde memory pressure oluyordu (12 MB DXF × 2-3 escape = ~30 MB string).
// Eski yazımları varsa silelim, slice initial state'i her zaman boş gelsin.
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem('baslikTemplates');
  }
} catch (e) { /* ignore */ }

function saveToStorage(/* templates, activeTemplateId */) {
  // No-op: backend'i kaynak kabul ediyoruz; localStorage devre dışı.
}

const stored = { templates: [], activeTemplateId: null };

const initialState = {
  templates: stored.templates || [],
  activeTemplateId: stored.activeTemplateId || null,         // başlık şablonu
  activeNotlarTemplateId: stored.activeNotlarTemplateId || null,  // notlar şablonu
  editor: {
    isOpen: false,
    dxfRawText: null,
    textEntities: [],    // [{ key:'text_0', type:'text', text:'...', position:{x,y} }]
    selectedEntityKey: null,
    pendingBindings: {},  // { 'text_0': { variablePath, originalText, entityType } }
    templateName: '',
    editingTemplateId: null,
  },
};

const baslikTemplateSlice = createSlice({
  name: 'baslikTemplate',
  initialState,
  reducers: {
    openEditor(state) {
      state.editor.isOpen = true;
      state.editor.dxfRawText = null;
      state.editor.textEntities = [];
      state.editor.selectedEntityKey = null;
      state.editor.pendingBindings = {};
      state.editor.templateName = '';
      state.editor.editingTemplateId = null;
    },

    closeEditor(state) {
      state.editor.isOpen = false;
      state.editor.dxfRawText = null;
      state.editor.textEntities = [];
      state.editor.selectedEntityKey = null;
      state.editor.pendingBindings = {};
      state.editor.templateName = '';
      state.editor.editingTemplateId = null;
    },

    setEditorDxf(state, action) {
      // payload: { dxfRawText, textEntities }
      state.editor.dxfRawText = action.payload.dxfRawText;
      state.editor.textEntities = action.payload.textEntities;
      state.editor.selectedEntityKey = null;
      state.editor.pendingBindings = {};
    },

    selectEntity(state, action) {
      // payload: entity key string (e.g. 'text_0')
      state.editor.selectedEntityKey = action.payload;
    },

    bindVariable(state, action) {
      // payload: { entityKey, variablePath, originalText, entityType }
      const { entityKey, variablePath, originalText, entityType } = action.payload;
      state.editor.pendingBindings[entityKey] = { variablePath, originalText, entityType };
    },

    unbindVariable(state, action) {
      // payload: entityKey
      delete state.editor.pendingBindings[action.payload];
    },

    setTemplateName(state, action) {
      state.editor.templateName = action.payload;
    },

    saveTemplate(state) {
      const { dxfRawText, pendingBindings, templateName, editingTemplateId } = state.editor;
      if (!dxfRawText || !templateName) return;

      if (editingTemplateId) {
        // Mevcut sablonu guncelle
        const idx = state.templates.findIndex(t => t.id === editingTemplateId);
        if (idx !== -1) {
          state.templates[idx].name = templateName;
          state.templates[idx].dxfRawText = dxfRawText;
          state.templates[idx].bindings = { ...pendingBindings };
        }
      } else {
        // Yeni sablon olustur
        const newTemplate = {
          id: 'tmpl_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
          name: templateName,
          createdAt: new Date().toISOString(),
          dxfRawText,
          bindings: { ...pendingBindings },
        };
        state.templates.push(newTemplate);
      }

      // Editor'u kapat
      state.editor.isOpen = false;
      state.editor.dxfRawText = null;
      state.editor.textEntities = [];
      state.editor.selectedEntityKey = null;
      state.editor.pendingBindings = {};
      state.editor.templateName = '';
      state.editor.editingTemplateId = null;

      saveToStorage(state.templates, state.activeTemplateId);
    },

    deleteTemplate(state, action) {
      // payload: template id
      state.templates = state.templates.filter(t => t.id !== action.payload);
      if (state.activeTemplateId === action.payload) {
        state.activeTemplateId = null;
      }
      saveToStorage(state.templates, state.activeTemplateId);
    },

    setActiveTemplate(state, action) {
      // payload: template id or null (null = varsayilan baslik)
      state.activeTemplateId = action.payload;
      saveToStorage(state.templates, state.activeTemplateId);
    },

    loadTemplateForEdit(state, action) {
      // payload: template id
      const tmpl = state.templates.find(t => t.id === action.payload);
      if (!tmpl) return;
      state.editor.isOpen = true;
      state.editor.dxfRawText = tmpl.dxfRawText;
      state.editor.textEntities = []; // Caller DXF'i tekrar parse edip textEntities set edecek
      state.editor.selectedEntityKey = null;
      state.editor.pendingBindings = { ...tmpl.bindings };
      state.editor.templateName = tmpl.name;
      state.editor.editingTemplateId = tmpl.id;
    },

    // Backend'den fetch edilmiş bir şablonu aktif et — slice'ı tek aktif şablonla doldur,
    // localStorage'a yazma (büyük DXF'ler quota'yı aşıyor; backend zaten kaynağı tutuyor).
    activateBackendTemplate(state, action) {
      // payload: { id, name, dxfRawText, bindings, marka, docId, subType, notesPayload }
      const { id, name, dxfRawText, bindings, marka, docId, subType, notesPayload } = action.payload;
      const tpl = {
        id,
        name,
        docId: docId || null,
        subType: subType || 'baslik',
        createdAt: new Date().toISOString(),
        dxfRawText: dxfRawText || '',
        bindings: bindings || {},
        marka: marka || null,   // native marka alanı ayarları (ole/native/circle/square/data)
        notesPayload: notesPayload || null,
      };
      // Aynı subType'tan eski entry'leri at, yenisini koy
      state.templates = state.templates.filter(t => t.subType !== tpl.subType);
      state.templates.push(tpl);
      if (tpl.subType === 'notlar') state.activeNotlarTemplateId = id;
      else state.activeTemplateId = id;
    },

    clearActiveTemplate(state, action) {
      // payload: 'baslik' | 'notlar' | undefined (her ikisi)
      const sub = action?.payload;
      if (sub === 'notlar') {
        state.activeNotlarTemplateId = null;
        state.templates = state.templates.filter(t => t.subType !== 'notlar');
      } else if (sub === 'baslik') {
        state.activeTemplateId = null;
        state.templates = state.templates.filter(t => t.subType === 'notlar');
      } else {
        state.activeTemplateId = null;
        state.activeNotlarTemplateId = null;
        state.templates = [];
      }
    },
  },
});

export const {
  openEditor,
  closeEditor,
  setEditorDxf,
  selectEntity,
  bindVariable,
  unbindVariable,
  setTemplateName,
  saveTemplate,
  deleteTemplate,
  setActiveTemplate,
  loadTemplateForEdit,
  activateBackendTemplate,
  clearActiveTemplate,
} = baslikTemplateSlice.actions;

// ─── Selectors ─────────────────────────────────────────
export const selectTemplates = (state) => state.baslikTemplate.templates;
export const selectActiveTemplateId = (state) => state.baslikTemplate.activeTemplateId;
export const selectActiveTemplate = (state) => {
  const id = state.baslikTemplate.activeTemplateId;
  if (!id) return null;
  return state.baslikTemplate.templates.find(t => t.id === id) || null;
};
export const selectActiveNotlarTemplateId = (state) => state.baslikTemplate.activeNotlarTemplateId;
export const selectActiveNotlarTemplate = (state) => {
  const id = state.baslikTemplate.activeNotlarTemplateId;
  if (!id) return null;
  return state.baslikTemplate.templates.find(t => t.id === id) || null;
};
export const selectEditorState = (state) => state.baslikTemplate.editor;
export const selectEditorTextEntities = (state) => state.baslikTemplate.editor.textEntities;
export const selectEditorBindings = (state) => state.baslikTemplate.editor.pendingBindings;

export default baslikTemplateSlice.reducer;
